import type {
  EventOptions,
  LoggedPartRequest,
  LogWorkoutRequest,
  RequestBody,
  ScoreInput,
  ScoreTypeOption,
} from "@repo/shared-types";
import { indices, partLabel, readParts, type Read } from "./event-form";
import { readScore } from "./measures";
import { MAX_NOTE } from "./results";
import { fromWallTime } from "./times";

/**
 * A workout of your own, read into what POST /api/t/me/entries takes: what it
 * was — a title, a sport and a format, its parts and how each is scored — and
 * how it went, in one form.
 *
 * Each part is named as the posting form names one (parts.0.title,
 * parts.0.scores.0.type, parts.0.timeLimit.min…), and its result beside it:
 * parts.0.results.0.min and so on, parts.0.level — "" for none, which leaves
 * the part without levels — parts.0.note and parts.0.feel. The api checks it
 * all again; this names the part the way the form does.
 */
export function readWorkout(
  form: FormData,
  zone: string,
  options: EventOptions,
): Read<RequestBody<LogWorkoutRequest>> {
  const text = (name: string) => String(form.get(name) ?? "").trim();

  const title = text("title");
  if (!title) return { error: "Give the workout a title.", field: "title" };
  const typedAt = text("performedAt");
  const performedAt = typedAt ? fromWallTime(typedAt, zone) : "";
  if (performedAt === null) return { error: "When you did it is not a time.", field: "performedAt" };

  // A part's own boxes are its sheet's, checked there: here it is a card.
  const parts = readLoggedParts(form, zone, options);
  if (parts.error !== undefined) return { error: parts.error };
  if (parts.value.length === 0) return { error: "Add at least one part." };

  const description = text("description");
  // No division: the api copies the profile's in, as for any result.
  return {
    value: {
      title,
      sportId: text("sportId"),
      formatId: text("formatId"),
      ...(description ? { description } : {}),
      ...(performedAt ? { performedAt } : {}),
      parts: parts.value,
    },
  };
}

/**
 * The workout's parts, each how it is scored and how it went: what a part's
 * sheet checks as it is saved, and the form reads as it is sent. `first` is
 * where the first sits among the workout's, so a message names it.
 */
export function readLoggedParts(
  form: FormData,
  zone: string,
  options: EventOptions,
  first = 0,
): Read<LoggedPartRequest[]> {
  const text = (name: string) => String(form.get(name) ?? "").trim();
  const described = readParts(form, zone, options, first);
  if (described.error !== undefined) return described;

  const parts: LoggedPartRequest[] = [];
  for (const [position, index] of indices(form, /^parts\.(\d+)\.title$/).entries()) {
    const part = described.value[position]!;
    const prefix = `parts.${index}`;
    const name = `Part ${partLabel(first + position)}`;
    const types = part.scores
      .map((score) => options.scoreTypes.find((option) => option.id === score.scoreTypeId))
      .filter((type): type is ScoreTypeOption => type !== undefined);
    const scored = types[0] !== undefined && types[0].measure !== "none";

    const results: ScoreInput[] = [];
    if (scored) {
      for (const [p, type] of types.entries()) {
        const score = readScore(form, `${prefix}.results.${p}`, type, name);
        if (score.error !== undefined) return score;
        results.push({
          value: score.value.value ?? null,
          reps: score.value.reps ?? null,
          capped: score.value.capped ?? null,
          unit: score.value.unit ?? null,
        });
      }
    }
    const recorded = results[0] !== undefined && (results[0].value !== null || results[0].capped === true);
    if (!recorded && results.some((score) => score.value !== null)) {
      return { error: `${name}: give its first score before its tiebreaks.`, field: `${prefix}.results.0` };
    }

    const note = text(`${prefix}.note`);
    if (note.length > MAX_NOTE) return { error: `${name}: a note is at most ${MAX_NOTE} characters.`, field: `${prefix}.note` };
    // A level chosen means the part has levels; none, that it has none.
    const level = scored ? text(`${prefix}.level`) : "";
    parts.push({
      title: part.title,
      description: part.description ?? null,
      timeLimitMs: part.timeLimitMs ?? null,
      usesLevels: level !== "",
      scores: part.scores,
      level: recorded && level ? level : null,
      // The badge rides on Rx alone.
      rxPlus: recorded && level === "rx" && form.get(`${prefix}.rxPlus`) !== null,
      results: scored ? results : null,
      note: note || null,
      feel: text(`${prefix}.feel`) || null,
    });
  }
  return { value: parts };
}
